/* ENSSA Reporting — Student page (Level 4). */

import React from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card, Eyebrow, Section,
} from "@/components/branded/Components";
import { PopulationPositionBar } from "@/components/charts/Charts";
import { StudentTaskFamilyTable } from "@/components/branded/StudentTaskFamilyTable";
import { useTracker } from "@/hooks/useTracker";
import {
  TERMS,
} from "@/data/tokens";
import {
  comparisonStudentsForScope,
  compositeScoreDistributionValues,
  scopeLabel,
} from "@/lib/charts";
import { compositeReportStatus, getStudentAssessment } from "@/lib/report-data";
import { getPopulationReferenceBandCutoffs } from "@/lib/population-reference-cutoffs";
import { PORTAL_TERMS } from "@/lib/copy";

import type { ClassRoom, District, School, Student, TermId, YearLevel } from "@/types";
import type { PortalPopulationReferenceResponse } from "@/contracts/portal";

interface PrintHeaderProps {
  school: School;
  klass: ClassRoom;
  student: Student;
  termLabel: string;
}

/* ── Print-only masthead ──
   Hidden on screen, shown @media print (see print.css). Renders student
   identity at the top of page 1 of the print artefact. */
function PrintHeader({ school, klass, student, termLabel }: PrintHeaderProps) {
  const subtitleParts = [school.name, klass.name, termLabel].filter(Boolean);
  return (
    <div className="print-header">
      <div className="print-header-logo-wrap">
        <img
          src="/assets/enssa-logo.png"
          alt="ENSSA — Early Number Sense Screener Australia"
          className="print-header-logo"
        />
      </div>
      <div className="print-header-title-wrap">
        <h1 style={{ fontSize: 26, fontWeight: 900, margin: 0, color: '#1B1729' }}>{student.name}</h1>
        <div style={{ fontSize: 13, color: '#6F6783', marginTop: 4 }}>
          {subtitleParts.join(' · ')}
        </div>

      </div>
    </div>
  );
}

const { useMemo: useSM } = React;

type CompareScope = 'class' | 'school' | 'population';

interface StudentPageProps {
  school: School;
  klass: ClassRoom;
  student: Student;
  term: TermId | string;
  districts?: District[];
  populationReference?: PortalPopulationReferenceResponse | null;
}

function recommendationText(studentName: string, risk: ReturnType<typeof getStudentAssessment>["risk"]): string {
  const first = studentName.split(' ')[0] ?? studentName;
  if (risk === 'low') {
    return `${first}'s performance on ENSSA is as expected for their year level. Continue with high quality class instruction and regular classroom assessment processes.`;
  }
  if (risk === 'moderate') {
    return `${first}'s performance is low for their year level. Further diagnostic assessment is recommended to pinpoint areas of individual difficulty, and target additional support.`;
  }
  if (risk === 'high') {
    return `${first}'s performance is very low for their year level. Detailed diagnostic assessment is recommended to pinpoint areas of individual difficulty, and targeted additional support is likely to be needed.`;
  }
  return `${first}'s ENSSA result is pending. Confirm task completion before drawing a conclusion.`;
}

function recommendationTone(risk: ReturnType<typeof getStudentAssessment>["risk"]): { border: string; bg: string } {
  if (risk === 'high') return { border: 'var(--risk-concern)', bg: 'var(--risk-concern-bg)' };
  if (risk === 'moderate') return { border: 'var(--risk-emerging)', bg: 'var(--risk-emerging-bg)' };
  if (risk === 'low') return { border: 'var(--risk-on-track)', bg: 'var(--risk-on-track-bg)' };
  return { border: 'var(--ink-300)', bg: 'var(--ink-50)' };
}

function displayScopeName(scope: CompareScope): string {
  if (scope === 'class') return 'Class';
  if (scope === 'school') return 'School';
  return 'Population';
}

function binnedPopulationValues(distribution: { bins?: { midpoint?: number; mid?: number; weight?: number; count?: number }[] } | null | undefined): number[] {
  const bins = distribution?.bins ?? [];
  const totalWeight = bins.reduce((sum, bin) => sum + Math.max(0, Number(bin.weight ?? bin.count ?? 0)), 0) || 1;
  const values: number[] = [];
  for (const bin of bins) {
    const midpoint = Number(bin.midpoint ?? bin.mid);
    const weight = Math.max(0, Number(bin.weight ?? bin.count ?? 0));
    if (!Number.isFinite(midpoint) || weight <= 0) continue;
    const repeats = Math.max(1, Math.round((weight / totalWeight) * 1000));
    for (let index = 0; index < repeats; index += 1) values.push(midpoint);
  }
  return values;
}

function populationReferenceValues(
  reference: PortalPopulationReferenceResponse | null | undefined,
  year: YearLevel,
  term: TermId | string,
): number[] {
  const direct = reference?.compositeScoreDistributions?.[year];
  if (direct?.length) return direct.filter((value) => Number.isFinite(value));
  const distributions = reference?.distributions;
  if (Array.isArray(distributions)) {
    const distribution = distributions.find((candidate) =>
      candidate.yearLevel === year && (!candidate.term || candidate.term === term),
    ) ?? distributions.find((candidate) => candidate.yearLevel === year);
    const values = distribution?.compositeScores ?? distribution?.scores ?? [];
    return values.filter((value) => Number.isFinite(value));
  }
  if (distributions && typeof distributions === 'object') {
    const byYear = (distributions as Partial<Record<YearLevel, { bins?: { midpoint?: number; mid?: number; weight?: number; count?: number }[] }>>)[year];
    return binnedPopulationValues(byYear);
  }
  return [];
}

function compareScopeFromParam(value: string | null): CompareScope {
  if (value === 'class' || value === 'school') return value;
  return 'population';
}

function firstNameOf(fullName: string): string {
  return fullName.split(' ')[0] ?? fullName;
}

function ordinalPercentile(percentile: number): string {
  const rounded = Math.max(1, Math.min(99, Math.round(percentile)));
  const lastTwo = rounded % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${rounded}th`;
  const last = rounded % 10;
  if (last === 1) return `${rounded}st`;
  if (last === 2) return `${rounded}nd`;
  if (last === 3) return `${rounded}rd`;
  return `${rounded}th`;
}

function percentileForScore(score: number, values: number[]): number | null {
  if (values.length === 0) return null;
  const atOrBelow = values.filter((value) => value <= score).length;
  return Math.max(1, Math.min(99, Math.round((atOrBelow / values.length) * 100)));
}

function studentScoreHeadline(args: {
  studentName: string;
  score: number;
  percentileRank: number | null;
  scope: CompareScope;
  yearLabel: string;
  className: string;
  schoolName: string;
}): string {
  const first = firstNameOf(args.studentName);
  const roundedScore = Math.round(args.score);
  if (args.percentileRank == null) {
    return `${first}'s standard ENSSA score is ${roundedScore}`;
  }
  const percentile = ordinalPercentile(args.percentileRank);
  if (args.scope === 'class') {
    return `${first}'s standard ENSSA score of ${roundedScore} is in the ${percentile} percentile in class ${args.className}`;
  }
  if (args.scope === 'school') {
    return `${first}'s standard ENSSA score of ${roundedScore} is in the ${percentile} percentile at ${args.schoolName}`;
  }
  return `${first}'s standard ENSSA score of ${roundedScore} is in the ${percentile} percentile among ${args.yearLabel} ENSSA students`;
}

function PercentileCutoffFootnote() {
  return (
    <>
      PR means Percentile Rank. Cutoffs are based on students in the same year level who took ENSSA: PR15 and below is{' '}
      <strong style={{ color: 'var(--risk-concern-fg)', whiteSpace: 'nowrap' }}>Very Low</strong>, PR16 to PR35 is{' '}
      <strong style={{ color: 'var(--risk-emerging-fg)', whiteSpace: 'nowrap' }}>Low</strong>, and PR36 and above is{' '}
      <strong style={{ color: 'var(--risk-on-track-fg)', whiteSpace: 'nowrap' }}>On Track</strong>.
    </>
  );
}

function studentStatusPill(risk: ReturnType<typeof getStudentAssessment>["risk"]): { label: string; bg: string; fg: string; border: string } {
  if (risk === 'high') {
    return {
      label: 'Very Low: PR15 and below',
      bg: 'var(--risk-concern-bg)',
      fg: 'var(--risk-concern-fg)',
      border: 'var(--risk-concern)',
    };
  }
  if (risk === 'moderate') {
    return {
      label: 'Low: PR16 to PR35',
      bg: 'var(--risk-emerging-bg)',
      fg: 'var(--risk-emerging-fg)',
      border: 'var(--risk-emerging)',
    };
  }
  if (risk === 'low') {
    return {
      label: 'On Track: PR36 and above',
      bg: 'var(--risk-on-track-bg)',
      fg: 'var(--risk-on-track-fg)',
      border: 'var(--risk-on-track)',
    };
  }
  return {
    label: 'No classification',
    bg: 'var(--ink-100)',
    fg: 'var(--fg-2)',
    border: 'var(--ink-300)',
  };
}

function StudentRecommendationCard({ studentName, risk }: { studentName: string; risk: ReturnType<typeof getStudentAssessment>["risk"] }) {
  const tone = recommendationTone(risk);
  return (
    <Card
      className="student-recommendation-callout"
      style={{
        marginBottom: 20,
        border: `1px solid ${tone.border}`,
        borderLeft: `6px solid ${tone.border}`,
        background: `linear-gradient(90deg, ${tone.bg} 0%, var(--ink-0) 68%)`,
        boxShadow: 'var(--sh-1)',
      }}
    >
      <Eyebrow>Recommendation</Eyebrow>
      <p style={{ margin: '8px 0 0', fontSize: 16, lineHeight: 1.55, color: 'var(--fg-1)', fontWeight: 600 }}>
        {recommendationText(studentName, risk)}
      </p>
    </Card>
  );
}

function StudentPage({ school, klass, student, term, districts = [], populationReference = null }: StudentPageProps) {
  const { track } = useTracker();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCompareScope = compareScopeFromParam(searchParams.get('compare'));
  const [isPrinting, setIsPrinting] = React.useState(false);
  const compareScope: CompareScope = isPrinting ? 'population' : selectedCompareScope;
  const setCompareScope = React.useCallback((next: CompareScope) => {
    if (next === selectedCompareScope) return;
    track("student_comparison_scope_changed", { scope: next });
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev);
      if (next === 'population') out.delete('compare');
      else out.set('compare', next);
      return out;
    }, { replace: true });
  }, [selectedCompareScope, setSearchParams, track]);
  const assessment = useSM(() => getStudentAssessment(student, term), [student, term]);
  const idx = assessment.compositeScore;
  const overallPercentile = assessment.overallPercentile;
  const reportStatus = compositeReportStatus(assessment);

  React.useEffect(() => {
    const startPrint = () => setIsPrinting(true);
    const endPrint = () => setIsPrinting(false);
    const media = window.matchMedia?.('print');
    const handleMediaChange = (event: MediaQueryListEvent) => setIsPrinting(event.matches);
    window.addEventListener('beforeprint', startPrint);
    window.addEventListener('afterprint', endPrint);
    media?.addEventListener?.('change', handleMediaChange);
    return () => {
      window.removeEventListener('beforeprint', startPrint);
      window.removeEventListener('afterprint', endPrint);
      media?.removeEventListener?.('change', handleMediaChange);
    };
  }, []);

  const comparisonStudents = useSM(
    () =>
      comparisonStudentsForScope(
        student,
        klass,
        school,
        districts,
        compareScope,
        term,
      ),
    [student, klass, school, districts, compareScope, term],
  );
  const referenceDistributionValues = useSM(
    () => populationReferenceValues(populationReference, student.year, term),
    [populationReference, student.year, term],
  );
  const distributionValues = useSM(
    () => {
      if (compareScope === 'population' && referenceDistributionValues.length > 0) return referenceDistributionValues;
      return compositeScoreDistributionValues(comparisonStudents, term);
    },
    [compareScope, referenceDistributionValues, comparisonStudents, term],
  );
  const compareScopeLabel = useSM(
    () => scopeLabel(compareScope, student, klass, school),
    [compareScope, student, klass, school],
  );
  const termLabel = useSM(
    () => TERMS.find((t) => t.id === term)?.label ?? '',
    [term],
  );
  const bandCutoffs = useSM(
    () => getPopulationReferenceBandCutoffs({ populationReference, year: student.year, term }),
    [populationReference, student.year, term],
  );
  const yearLabel = klass.year === 'F' ? 'Foundation' : 'Year 1';
  const comparisonPercentile = useSM(
    () => {
      if (idx == null) return null;
      if (compareScope === 'population') return overallPercentile;
      return percentileForScore(idx, distributionValues) ?? overallPercentile;
    },
    [idx, compareScope, distributionValues, overallPercentile],
  );
  const headline = useSM(
    () => (idx == null ? '' : studentScoreHeadline({
      studentName: student.name,
      score: idx,
      percentileRank: comparisonPercentile,
      scope: compareScope,
      yearLabel,
      className: klass.name,
      schoolName: school.name,
    })),
    [student.name, idx, comparisonPercentile, compareScope, yearLabel, klass.name, school.name],
  );
  const statusPill = useSM(() => studentStatusPill(assessment.risk), [assessment.risk]);

  return (
    <>
      <PrintHeader school={school} klass={klass} student={student} termLabel={termLabel} />

      <section className="print-page-1">

      {reportStatus !== 'suppressed' && idx != null && (
        <StudentRecommendationCard studentName={student.name} risk={assessment.risk} />
      )}

      {/* ── Score curve with percentile-rank interpretation. */}
      {reportStatus !== 'suppressed' && idx != null && (
        <Card style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 16 }}>
            <div style={{ minWidth: 260, flex: '1 1 420px' }}>
              <h2 style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.2, margin: 0, color: 'var(--fg-1)', maxWidth: 860 }}>
                {headline}
              </h2>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  marginTop: 10,
                  padding: '7px 11px',
                  borderRadius: 999,
                  background: statusPill.bg,
                  color: statusPill.fg,
                  fontSize: 13,
                  fontWeight: 900,
                  lineHeight: 1.1,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 11,
                    height: 11,
                    borderRadius: '50%',
                    background: statusPill.border,
                    flexShrink: 0,
                  }}
                />
                {statusPill.label}
              </span>
            </div>
            <div
              role="group"
              aria-label="Choose background comparison curve"
              className="compare-toggle"
              style={{ display: 'inline-flex', gap: 4, padding: 4, borderRadius: 999, background: 'var(--ink-100)', border: '1px solid var(--border-subtle)' }}
            >
              {(['class', 'school', 'population'] as const).map((scope) => {
                const active = compareScope === scope;
                return (
                  <button
                    key={scope}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setCompareScope(scope)}
                    style={{
                      minHeight: 34,
                      padding: '0 12px',
                      borderRadius: 999,
                      border: active ? '1px solid var(--enssa-aubergine)' : '1px solid transparent',
                      background: active ? 'var(--enssa-aubergine)' : 'transparent',
                      color: active ? 'var(--ink-0)' : 'var(--fg-2)',
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    {displayScopeName(scope)}
                  </button>
                );
              })}
            </div>
          </div>
          {reportStatus === 'provisional' && (
            <div
              role="note"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 9,
                margin: '0 0 14px',
                color: 'var(--fg-2)',
                fontSize: 13,
                fontWeight: 700,
                lineHeight: 1.45,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 22,
                  height: 22,
                  borderRadius: 999,
                  background: 'var(--ink-100)',
                  color: 'var(--enssa-aubergine)',
                  fontSize: 12,
                  fontWeight: 900,
                  flexShrink: 0,
                }}
              >
                i
              </span>
              <span>One subtest is incomplete, so this score is less precise than a complete result.</span>
            </div>
          )}
          <PopulationPositionBar
            score={idx}
            intervalLow={null}
            intervalHigh={null}
            distributionValues={distributionValues}
            sampleSize={distributionValues.length}
            scopeKind={compareScope}
            scopeLabel={compareScopeLabel}
            percentileRank={comparisonPercentile}
            scaleNote={<PercentileCutoffFootnote />}
            bandCutoffs={bandCutoffs}
          />
        </Card>
      )}

      {reportStatus === 'suppressed' && (
        <Card style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.2, margin: 0, color: 'var(--fg-1)', maxWidth: 860 }}>
            Overall ENSSA score not reported
          </h2>
          <p style={{ margin: '12px 0 0', color: 'var(--fg-2)', fontSize: 14, lineHeight: 1.55, fontWeight: 700 }}>
            This student needs to complete the tasks before an overall result can be shown.
          </p>
        </Card>
      )}

      </section>

      <section className="print-page-2">

      {/* ── Task-family performance ── */}
      <Section title={`${PORTAL_TERMS.task} Performance`}>
        <Card style={{ padding: 0 }}>
          <StudentTaskFamilyTable student={student} klass={klass} term={term} />
        </Card>
      </Section>

      </section>

    </>
  );
}

export { StudentPage };
export default StudentPage;
